import { useState, useEffect } from "react";
import { Form } from "react-admin";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "@mui/styles";
import DismissibleMessage from "../DismissibleMessage";
import { ClientsDropdown, ProjectsDropdown } from "../DropdownV2";
import { calibrationService } from "../../../../services/CalibrationService";
import { formatCalibrationDttmUTC } from "../util";
import { PIT_GEOJSON_TILES_DATASET_TYPE, TILE_GENERATION_PROCESS_TYPE } from "./tileStatus";
import TileQAMap from "./TileQAMap";

const DEFAULT_GROUP_KEY = "default";

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    dropdownRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: theme.spacing(2),
        margin: "10px 0",
        width: "100%",
        maxWidth: "700px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    statusCard: {
        width: "100%",
        maxWidth: "700px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px",
        margin: "12px 0",
        backgroundColor: "#fafafa",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },
    statusRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    buttonRow: {
        display: "flex",
        justifyContent: "center",
        margin: "20px 0",
    },
}));

export default function SingleProjectTileGen() {
    const classes = useStyles();
    const [clientName, setClientName] = useState(null);
    const [projectName, setProjectName] = useState(null);
    const [tilesAvailable, setTilesAvailable] = useState(null); // null = checking, true/false = result
    const [lastTilesDttm, setLastTilesDttm] = useState(null);
    const [tilesUrl, setTilesUrl] = useState(null);
    const [loadingTiles, setLoadingTiles] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [triggered, setTriggered] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        setProjectName(null);
    }, [clientName]);

    // On client+project change: fetch the latest pit-geojson-tiles dataset's
    // signed S3 URL; hand it to TileQAMap which downloads + renders.
    useEffect(() => {
        if (!clientName || !projectName) {
            setTilesAvailable(null);
            setLastTilesDttm(null);
            setTilesUrl(null);
            setTriggered(false);
            setErrorMessage(null);
            return;
        }
        let cancelled = false;
        setLoadingTiles(true);
        setTilesAvailable(null);
        setTilesUrl(null);
        setTriggered(false);
        setErrorMessage(null);

        (async () => {
            try {
                const latest = await calibrationService.getLatestDataset(
                    clientName,
                    projectName,
                    DEFAULT_GROUP_KEY,
                    PIT_GEOJSON_TILES_DATASET_TYPE,
                    false
                );
                if (cancelled) return;
                const dttm = latest?.meta?.version_dttm;
                if (!dttm) {
                    setTilesAvailable(false);
                    return;
                }
                setLastTilesDttm(dttm);
                const signed = await calibrationService.getDatasetSignedUrl(
                    clientName,
                    projectName,
                    PIT_GEOJSON_TILES_DATASET_TYPE,
                    DEFAULT_GROUP_KEY,
                    dttm
                );        
                if (cancelled) return;
                setTilesUrl(signed?.url || null);
                setTilesAvailable(!!signed?.url);
            } catch (err) {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    setTilesAvailable(false);
                } else {
                    setErrorMessage(`Failed to load tiles: ${err?.message || String(err)}`);
                    setTilesAvailable(false);
                }
            } finally {
                if (!cancelled) setLoadingTiles(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [clientName, projectName]);

    const handleTrigger = async () => {
        setErrorMessage(null);
        setTriggering(true);
        try {
            await calibrationService.triggerTileGeneration(clientName, projectName);
            setTriggered(true);
        } catch (err) {
            setErrorMessage(`Failed to submit: ${err?.message || String(err)}`);
        } finally {
            setTriggering(false);
        }
    };

    const progressViewerUrl = `/calibration/progressViewer?client=${encodeURIComponent(
        clientName || ""
    )}&project=${encodeURIComponent(projectName || "")}&processTypes=${TILE_GENERATION_PROCESS_TYPE}`;

    const canTrigger = !!clientName && !!projectName && !triggering;

    return (
        <div style={{ paddingBottom: "200px" }}>
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />

                <Typography
                    variant="body2"
                    style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto 8px", color: "#666" }}
                >
                    Manually (re)generate PIT GeoJSON tiles for a single project. Phase 1 already produces tiles
                    automatically; use this when you need to refresh tiles from an existing <code>pit-geojson</code>{" "}
                    without re-running Phase 1.
                </Typography>

                <div className={classes.dropdownRow}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={setClientName}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                    <ProjectsDropdown
                        clientName={clientName}
                        value={projectName}
                        setValue={setProjectName}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                </div>

                {clientName && projectName && (
                    <div className={classes.statusCard}>
                        <div className={classes.statusRow}>
                            {loadingTiles && (
                                <>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2">Checking for existing tiles…</Typography>
                                </>
                            )}
                            {!loadingTiles && tilesAvailable === true && (
                                <Typography variant="body2">
                                    Tiles available
                                    {lastTilesDttm && (
                                        <span style={{ color: "#666", marginLeft: 8 }}>
                                            (last updated {formatCalibrationDttmUTC(lastTilesDttm)})
                                        </span>
                                    )}
                                </Typography>
                            )}
                            {!loadingTiles && tilesAvailable === false && (
                                <Typography variant="body2" color="textSecondary">
                                    No tiles found for this project yet.
                                </Typography>
                            )}
                        </div>
                        {triggered && (
                            <Typography variant="body2" color="primary">
                                Tile generation submitted — check the{" "}
                                <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">
                                    Progress Viewer
                                </Link>{" "}
                                for status.
                            </Typography>
                        )}
                        {!triggered && (
                            <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">
                                View in Progress Viewer
                            </Link>
                        )}
                    </div>
                )}

                <div className={classes.buttonRow}>
                    <Button variant="contained" disabled={!canTrigger} onClick={handleTrigger}>
                        {triggering
                            ? "Submitting…"
                            : tilesAvailable === true
                            ? "Regenerate Tiles"
                            : "Generate Tiles"}
                    </Button>
                </div>

                {tilesUrl && (
                    <div style={{ width: "100%", maxWidth: "900px" }}>
                        <TileQAMap tilesUrl={tilesUrl} height={500} />
                    </div>
                )}
            </Form>
        </div>
    );
}
